import {
    ActivityCategory,
    activityCategoryLabels,
    activityCategoryLogLabels,
    ActivityRecordType,
    ActivityTargetView,
    categoryActivityTarget,
    genericLogLabel,
    ReferenceBusinessId,
    ReferenceContactId,
    serializeActivityTarget,
    taskTypeActivityTarget,
    TaskTypeId,
} from "@sp-crm/core";
import {
    EntityDetails,
    labeledContactPhones,
} from "components/details-page-v-next/entity-contact-details";
import { RecentActivityFeedItem } from "components/details-page-v-next/recent-activities-feed";
import {
    ActivityTypeOption,
    activityTypesForRecordType,
} from "components/tasks/activity-type-options";
import { VisitParent } from "components/visits/types";
import { TouchpointStatusView } from "./marketing-plan-format";

export const marketingPlanRecordType: ActivityRecordType = "referenceContact";

export const activityTypesForMarketingPlan = (
    activityTypes: ActivityTypeOption[],
    targets: ActivityTargetView[],
): ActivityTypeOption[] =>
    activityTypesForRecordType(
        activityTypes,
        marketingPlanRecordType,
        targets.flatMap(target => (target.taskTypeId ? [target.taskTypeId] : [])),
    );

export interface AgendaFeedTouchpoint {
    touchpointId: string;
    targets: ActivityTargetView[];
    intervalCount: number;
    intervalUnit: string;
    state?: TouchpointStatusView["state"];
    lastContactedAt?: string | null;
    nextDueAt?: string | null;
    pendingVisit?: TouchpointStatusView["pendingVisit"];
}

export interface AgendaFeedContact {
    contactId: ReferenceContactId;
    contactName: string;
    orgName?: string | null;
    contactEmail?: string | null;
    contactPhone?: string | null;
    contactCellPhone?: string | null;
    contactAltPhone?: string | null;
    contactAddress?: string | null;
    contactEmailOptedOut: boolean;
    tierListItemId: string;
    tierName: string;
    tierColor?: string | null;
    touchpoints: AgendaFeedTouchpoint[];
    satisfiedTouchpoints?: AgendaFeedTouchpoint[];
    recentActivity: AgendaRecentActivityItem[];
}

export interface OrgAgendaContactOption {
    id: string;
    name: string;
    phone: string;
}

export interface AgendaFeedOrg {
    businessId: string;
    businessName: string;
    businessEmail?: string | null;
    businessPhone?: string | null;
    businessAddress?: string | null;
    tierListItemId: string;
    tierName: string;
    tierColor?: string | null;
    contacts: OrgAgendaContactOption[];
    touchpoints: AgendaFeedTouchpoint[];
    satisfiedTouchpoints?: AgendaFeedTouchpoint[];
    recentActivity: AgendaRecentActivityItem[];
}

export interface AgendaFeed {
    contacts: AgendaFeedContact[];
    orgs: AgendaFeedOrg[];
    completedContactIds: string[];
    completedBusinessIds: string[];
}

export interface AgendaTouchpoint extends TouchpointStatusView {
    touchpointId: string;
    targets: ActivityTargetView[];
    intervalCount: number;
    intervalUnit: string;
}

export const targetCategories = (targets: ActivityTargetView[]): Set<ActivityCategory> =>
    new Set(targets.map(target => target.category));

export interface LoggedActivityChoice {
    key: string;
    label: string;
    category: ActivityCategory;
    taskTypeId: TaskTypeId | null;
}

export interface LoggedActivityGroup {
    category: ActivityCategory;
    label: string;
    choices: LoggedActivityChoice[];
}

const typeChoice = (option: ActivityTypeOption): LoggedActivityChoice => ({
    key: serializeActivityTarget(taskTypeActivityTarget(option.id)),
    label: option.name,
    category: option.category,
    taskTypeId: option.id,
});

const wholeCategoryChoice = (category: ActivityCategory): LoggedActivityChoice => ({
    key: serializeActivityTarget(categoryActivityTarget(category)),
    label: activityCategoryLabels[category],
    category,
    taskTypeId: null,
});

export const activityChoicesForCategory = (
    targets: ActivityTargetView[],
    category: ActivityCategory,
    activityTypes: ActivityTypeOption[],
): LoggedActivityChoice[] => {
    const inCategory = targets.filter(target => target.category === category);
    const scopedTypeIds = inCategory.flatMap(target =>
        target.kind === "taskType" ? [target.taskTypeId] : [],
    );
    if (!inCategory.some(target => target.kind === "category")) {
        return activityTypes
            .filter(option => scopedTypeIds.includes(option.id))
            .map(typeChoice);
    }
    const offerable = activityTypes.filter(
        option =>
            option.category === category &&
            (!option.isArchived || scopedTypeIds.includes(option.id)),
    );
    return offerable.length > 0
        ? offerable.map(typeChoice)
        : [wholeCategoryChoice(category)];
};

export const loggedActivityGroups = (
    targets: ActivityTargetView[],
    activityTypes: ActivityTypeOption[],
): LoggedActivityGroup[] => {
    const categories: ActivityCategory[] = [];
    for (const target of targets) {
        if (!categories.includes(target.category)) {
            categories.push(target.category);
        }
    }
    return categories.map(category => ({
        category,
        label: activityCategoryLabels[category],
        choices: activityChoicesForCategory(targets, category, activityTypes),
    }));
};

export const loggedActivityChoices = (
    groups: LoggedActivityGroup[],
): LoggedActivityChoice[] => groups.flatMap(group => group.choices);

export interface AgendaContactGroup {
    contactId: ReferenceContactId;
    contactName: string;
    orgName: string | null;
    contactEmail: string | null;
    contactPhone: string | null;
    contactCellPhone: string | null;
    contactAltPhone: string | null;
    contactAddress: string | null;
    contactEmailOptedOut: boolean;
    tierListItemId: string;
    tierName: string | null;
    tierColor: string | null;
    touchpoints: AgendaTouchpoint[];
    recentActivity: AgendaRecentActivityItem[];
}

const toAgendaTouchpoint = (touchpoint: AgendaFeedTouchpoint): AgendaTouchpoint => ({
    touchpointId: touchpoint.touchpointId,
    targets: touchpoint.targets,
    intervalCount: touchpoint.intervalCount,
    intervalUnit: touchpoint.intervalUnit,
    lastContactedAt: touchpoint.lastContactedAt ?? null,
    nextDueAt: touchpoint.nextDueAt ?? null,
    state: touchpoint.state ?? "due",
    pendingVisit: touchpoint.pendingVisit ?? null,
});

export const agendaContactGroups = (
    contacts: AgendaFeedContact[],
): AgendaContactGroup[] =>
    contacts.map(contact => ({
        contactId: contact.contactId,
        contactName: contact.contactName,
        orgName: contact.orgName ?? null,
        contactEmail: contact.contactEmail ?? null,
        contactPhone: contact.contactPhone ?? null,
        contactCellPhone: contact.contactCellPhone ?? null,
        contactAltPhone: contact.contactAltPhone ?? null,
        contactAddress: contact.contactAddress ?? null,
        contactEmailOptedOut: contact.contactEmailOptedOut,
        tierListItemId: contact.tierListItemId,
        tierName: contact.tierName,
        tierColor: contact.tierColor ?? null,
        touchpoints: [
            ...contact.touchpoints,
            ...(contact.satisfiedTouchpoints ?? []),
        ].map(toAgendaTouchpoint),
        recentActivity: contact.recentActivity,
    }));

export interface AgendaOrgGroup {
    businessId: string;
    businessName: string;
    businessEmail: string | null;
    businessPhone: string | null;
    businessAddress: string | null;
    tierListItemId: string;
    tierName: string | null;
    tierColor: string | null;
    contacts: OrgAgendaContactOption[];
    touchpoints: AgendaTouchpoint[];
    recentActivity: AgendaRecentActivityItem[];
}

export const agendaOrgGroups = (orgs: AgendaFeedOrg[]): AgendaOrgGroup[] =>
    orgs.map(org => ({
        businessId: org.businessId,
        businessName: org.businessName,
        businessEmail: org.businessEmail ?? null,
        businessPhone: org.businessPhone ?? null,
        businessAddress: org.businessAddress ?? null,
        tierListItemId: org.tierListItemId,
        tierName: org.tierName,
        tierColor: org.tierColor ?? null,
        contacts: org.contacts,
        touchpoints: [...org.touchpoints, ...(org.satisfiedTouchpoints ?? [])].map(
            toAgendaTouchpoint,
        ),
        recentActivity: org.recentActivity,
    }));

export const agendaContactDetails = (group: AgendaContactGroup): EntityDetails => ({
    classification: null,
    classificationLabel: "Role",
    phones: labeledContactPhones({
        cell: group.contactCellPhone,
        alternate: group.contactAltPhone,
    }),
    fax: null,
    email: group.contactEmail,
    emailOptOut: group.contactEmailOptedOut,
    address: group.contactAddress,
    website: null,
    organization: null,
});

export const agendaOrgDetails = (group: AgendaOrgGroup): EntityDetails => ({
    classification: null,
    classificationLabel: "Role",
    phones: labeledContactPhones({ cell: group.businessPhone, alternate: null }),
    fax: null,
    email: group.businessEmail,
    emailOptOut: false,
    address: group.businessAddress,
    website: null,
    organization: null,
});

export interface AgendaCard {
    group: AgendaContactGroup;
    isCelebrating: boolean;
}

export interface AgendaMergeInput {
    groups: AgendaContactGroup[];
    snoozedContactIds: string[];
    completedContactIds: string[];
}

interface AgendaCardState<TGroup> {
    group: TGroup;
    isCelebrating: boolean;
}

interface AgendaGroupMergeInput<TGroup> {
    groups: TGroup[];
    snoozedIds: string[];
    completedIds: string[];
}

const mergeAgendaGroupCards = <TGroup>(
    previous: AgendaCardState<TGroup>[],
    incoming: AgendaGroupMergeInput<TGroup>,
    idOf: (group: TGroup) => string,
): AgendaCardState<TGroup>[] => {
    const byId = new Map(incoming.groups.map(group => [idOf(group), group]));
    const snoozed = new Set(incoming.snoozedIds);
    const completed = new Set(incoming.completedIds);
    const kept = previous.flatMap(card => {
        const id = idOf(card.group);
        const group = byId.get(id);
        if (group) {
            return [{ group, isCelebrating: false }];
        }
        return !snoozed.has(id) && completed.has(id)
            ? [{ group: card.group, isCelebrating: true }]
            : [];
    });
    const shown = new Set(kept.map(card => idOf(card.group)));
    const added = incoming.groups
        .filter(group => !shown.has(idOf(group)))
        .map(group => ({ group, isCelebrating: false }));
    return [...kept, ...added];
};

export const mergeAgendaCards = (
    previous: AgendaCard[],
    incoming: AgendaMergeInput,
): AgendaCard[] =>
    mergeAgendaGroupCards(
        previous,
        {
            groups: incoming.groups,
            snoozedIds: incoming.snoozedContactIds,
            completedIds: incoming.completedContactIds,
        },
        group => group.contactId,
    );

export interface AgendaOrgCardState {
    group: AgendaOrgGroup;
    isCelebrating: boolean;
}

export interface AgendaOrgMergeInput {
    groups: AgendaOrgGroup[];
    snoozedBusinessIds: string[];
    completedBusinessIds: string[];
}

export const mergeAgendaOrgCards = (
    previous: AgendaOrgCardState[],
    incoming: AgendaOrgMergeInput,
): AgendaOrgCardState[] =>
    mergeAgendaGroupCards(
        previous,
        {
            groups: incoming.groups,
            snoozedIds: incoming.snoozedBusinessIds,
            completedIds: incoming.completedBusinessIds,
        },
        group => group.businessId,
    );

export type AgendaRecentActivityItem = RecentActivityFeedItem;

export const filterCardsByTier = (
    cards: AgendaCard[],
    selectedTierIds: string[],
): AgendaCard[] => {
    if (selectedTierIds.length === 0) {
        return cards;
    }
    const selected = new Set(selectedTierIds);
    return cards.filter(card => selected.has(card.group.tierListItemId));
};

export const filterOrgCardsByTier = (
    cards: AgendaOrgCardState[],
    selectedTierIds: string[],
): AgendaOrgCardState[] => {
    if (selectedTierIds.length === 0) {
        return cards;
    }
    const selected = new Set(selectedTierIds);
    return cards.filter(card => selected.has(card.group.tierListItemId));
};

export type AgendaEntry =
    | { kind: "contact"; card: AgendaCard }
    | { kind: "organization"; card: AgendaOrgCardState };

const dueKeyForTouchpoints = (touchpoints: AgendaTouchpoint[]): number => {
    const due =
        touchpoints.find(touchpoint => touchpoint.state === "due") ?? touchpoints[0];
    if (!due?.nextDueAt) {
        return Number.NEGATIVE_INFINITY;
    }
    return new Date(due.nextDueAt).getTime();
};

const entryDueKey = (entry: AgendaEntry): number =>
    dueKeyForTouchpoints(entry.card.group.touchpoints);

export const mergeAgendaEntries = (
    cards: AgendaCard[],
    orgCards: AgendaOrgCardState[],
): AgendaEntry[] => {
    const entries: AgendaEntry[] = [
        ...cards.map(card => ({ kind: "contact" as const, card })),
        ...orgCards.map(card => ({ kind: "organization" as const, card })),
    ];
    return entries.sort((a, b) => entryDueKey(a) - entryDueKey(b));
};

export const logLabelForCategory = (category: ActivityCategory | null): string =>
    category ? activityCategoryLogLabels[category] : genericLogLabel;

export type EmailActionState = "send-email" | "add-email" | "email-opted-out";

export const resolveEmailAction = (input: {
    email: string | null;
    emailOptedOut: boolean;
}): EmailActionState => {
    if (input.emailOptedOut) {
        return "email-opted-out";
    }
    if (input.email) {
        return "send-email";
    }
    return "add-email";
};

export interface TouchpointActions {
    email: EmailActionState | null;
    addContactPhone: boolean;
    provisionPhoneLine: boolean;
    call: boolean;
    text: boolean;
    visit: boolean;
}

export const resolveTouchpointActions = (input: {
    targets: ActivityTargetView[];
    email: string | null;
    emailOptedOut: boolean;
    hasPhone: boolean;
    hasPhoneLine: boolean;
}): TouchpointActions => {
    const categories = targetCategories(input.targets);
    const wantsCall = categories.has("call");
    const wantsText = categories.has("sms");
    const wantsPhone = wantsCall || wantsText;
    const callable = wantsPhone && input.hasPhone && input.hasPhoneLine;
    return {
        email: categories.has("email") ? resolveEmailAction(input) : null,
        addContactPhone: wantsPhone && !input.hasPhone,
        provisionPhoneLine: wantsPhone && input.hasPhone && !input.hasPhoneLine,
        call: wantsCall && callable,
        text: wantsText && callable,
        visit: categories.has("visit"),
    };
};

export type TouchpointLogParent =
    | { kind: "contact"; contactId: ReferenceContactId }
    | { kind: "business"; businessId: ReferenceBusinessId };

export const toTouchpointLogParent = (parent: VisitParent): TouchpointLogParent => {
    switch (parent.kind) {
        case "contact":
            return { kind: "contact", contactId: parent.id };
        case "organization":
            return { kind: "business", businessId: parent.id };
        default: {
            const exhaustiveCheck: never = parent;
            throw new Error(`Unhandled VisitParent kind ${String(exhaustiveCheck)}`);
        }
    }
};

export const agendaNeedsPhoneProvisioning = (
    groups: { touchpoints: AgendaTouchpoint[] }[],
    hasPhoneLine: boolean,
): boolean => {
    if (hasPhoneLine) {
        return false;
    }
    return groups.some(group =>
        group.touchpoints.some(touchpoint => {
            if (touchpoint.state !== "due") {
                return false;
            }
            const categories = targetCategories(touchpoint.targets);
            return categories.has("call") || categories.has("sms");
        }),
    );
};
